import { useState } from "react";
import { useInstallPrompt } from "../lib/installPrompt";

const DISMISSED_KEY = "prepai.install.dismissed";

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * "Install PrepAI" card on Home.
 *
 * Shown only when installing is possible: the browser handed us its install
 * prompt, or it is iOS Safari, where the student has to use Share → Add to
 * Home Screen themselves. Hidden once installed or dismissed — asking again on
 * every open is exactly the nagging UX decision 7 rules out.
 */
export function InstallCard() {
  const { canPrompt, needsManualSteps, installed, install } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(wasDismissed);
  const [showSteps, setShowSteps] = useState(false);

  if (installed || dismissed || (!canPrompt && !needsManualSteps)) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      /* Hidden for this session at least. */
    }
    setDismissed(true);
  };

  return (
    <div className="mt-3 flex flex-col gap-2 rounded-[var(--radius-card)] border border-border bg-surface p-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-[13.5px] font-semibold">Install PrepAI</span>
          <span className="text-[12px] leading-[1.5] text-text-muted">
            Open it from your home screen and practise offline.
          </span>
        </div>
        <button
          type="button"
          aria-label="Dismiss install"
          onClick={dismiss}
          className="shrink-0 text-[14px] text-text-muted"
        >
          ✕
        </button>
      </div>

      {canPrompt ? (
        <button
          type="button"
          onClick={() => void install()}
          className="min-h-11 w-full rounded-[var(--radius-chip)] border border-accent bg-accent-tint px-3 text-[12.5px] font-semibold text-accent"
        >
          Install app
        </button>
      ) : showSteps ? (
        <p className="m-0 text-[12.5px] leading-[1.5] text-text">
          Tap the Share button <span aria-hidden="true">⎋</span> in Safari, then{" "}
          <strong>Add to Home Screen</strong>.
        </p>
      ) : (
        <button
          type="button"
          onClick={() => setShowSteps(true)}
          className="min-h-11 w-full rounded-[var(--radius-chip)] border border-accent bg-accent-tint px-3 text-[12.5px] font-semibold text-accent"
        >
          How to install
        </button>
      )}
    </div>
  );
}
